import { Box, Paper, Typography } from "@mui/material";
import DOMPurify from "dompurify";
import * as React from "react";
import { getSessionCookie } from "../../components/cookies";
import * as Service from "../../services/release_note";
import { commonState } from "../../store";
import { colors, fontSizes } from "../../theme";
import { tsToFormattedDate } from "../../utils/humanize";
import { State } from "./store";

export interface Props {
    state: State;
    common: commonState;
}

export const ReleaseNoteContainer: React.FC<Props> = (props: Props) => {
    React.useEffect(() => {
        if (!getSessionCookie().consoleUserId) return;
        Service.getReleaseNotes();
    }, [getSessionCookie().consoleUserId]);

    const notes = props.state.releaseNotes;

    return (
        <>
            {notes?.length == 0 && (
                <Paper
                    variant="outlined"
                    sx={{ p: 10, mb: 10, textAlign: "center" }}
                >
                    参照可能なリリースノートはありません
                </Paper>
            )}
            {notes?.map((note, i) => (
                <Paper key={i} variant="outlined" sx={{ p: 5, mb: 10 }}>
                    <Box
                        sx={{
                            mb: 3,
                            color: colors.gray.main,
                            fontSize: fontSizes.large,
                        }}
                    >
                        {tsToFormattedDate(note.publishedAt)}
                    </Box>
                    <Typography variant="h2">{note.title}</Typography>
                    <Box
                        sx={{
                            mt: 5,
                            strong: {
                                fontWeight: "bold",
                            },
                            "ul, ol": {
                                paddingInlineStart: "21px",
                                "> li": {
                                    listStyleType: "inherit",
                                    ul: {
                                        listStyleType: "circle",
                                    },
                                },
                            },
                            ul: {
                                listStyleType: "disc",
                            },
                            "h1, h2, h3, h4, h5, h6": {
                                mt: 0,
                                mb: 3,
                            },
                            h1: {
                                fontSize: fontSizes.xlarge,
                            },
                            "h2, h5": {
                                fontWeight: "bold",
                            },
                            "h2, h3": {
                                fontSize: fontSizes.large,
                            },
                            "h4, h5": {
                                fontSize: fontSizes.normal,
                            },
                            h6: {
                                fontSize: fontSizes.small,
                            },
                            p: {
                                fontSize: fontSizes.normal,
                                color: "inherit",
                            },
                            blockquote: {
                                m: 0,
                                pl: 4,
                                color: colors.gray.b20,
                                borderColor: colors.gray.main,
                                borderLeft: "solid",
                                borderWidth: 4,
                            },
                            table: {
                                mt: 3,
                                mb: 3,
                                width: "100%",
                                display: "table",
                                border: "none",
                                borderCollapse: "collapse",
                                borderSpacing: 0,
                                "th, td": {
                                    p: 1,
                                },
                                th: {
                                    backgroundColor: colors.gray.f90,
                                },
                                td: {
                                    backgroundColor: colors.white.main,
                                },
                                "tr, th, td": {
                                    border: `1px solid ${colors.gray.a50}`,
                                },
                            },
                            a: {
                                color: colors.green.main,
                                "&:hover": {
                                    color: colors.green.b20,
                                },
                            },
                        }}
                        dangerouslySetInnerHTML={{
                            __html: DOMPurify.sanitize(note.markedBody, {}),
                        }}
                    />
                    {Boolean(note.hasImage) && (
                        <Box
                            sx={{
                                mt: 3,
                                maxWidth: "100%",
                                verticalAlign: "top", // remove image bottom margin
                            }}
                            component="img"
                            src={`/v1/release_notes/${note.id}/image.${note.imageExt}`}
                            alt={`${note.title}のイメージ画像`}
                        />
                    )}
                </Paper>
            ))}
        </>
    );
};
